import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAuth, useAutoSignin} from "react-oidc-context";
import {basePath} from "../utils/basePath";
import {useLocation, useNavigate} from "react-router-dom";
import {AuthSetup} from "../authConfig";

export const useKeyCloakAuth = (authSetup: AuthSetup) => {
    const {user, isAuthenticated, isLoading, error, signinRedirect, signoutSilent, events, removeUser, signinSilent} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const userIdMemo = useMemo(() => user?.profile.email, [user?.profile.email]);

    const tokenRef = useRef<string | null>(null);
    const [hasToken, setHasToken] = useState<boolean>(false);

    useEffect(() => {
        console.log("KeyCloakAuth: updating access token in ref");
        tokenRef.current = user?.access_token ?? null;
        const hasTokenNow = user?.access_token != null;
        if (hasToken !== hasTokenNow) {
            setHasToken(hasTokenNow);
        }
    }, [user?.access_token, hasToken]);

    const removeUserOnError = useCallback(async () => {
        await removeUser();
    }, [removeUser]);

    const silentRenewErrorHandler = useCallback(async (e) => {
        console.log("KeyCloakAuth: error during silent renewal", e);
        await removeUserOnError()
    }, [removeUserOnError]);

    useEffect(() => {
        const signinIgnoringErrors = async () => {
            try {
                await signinSilent();
            } catch (e) {
                console.log(`KeyCloakAuth: Failed to sign in silently: ${e}`);
            }
        }
        events.addSilentRenewError(silentRenewErrorHandler);
        signinIgnoringErrors()
        return () => {
            events.removeSilentRenewError(silentRenewErrorHandler);
        }
    }, [removeUserOnError, events, signinSilent, silentRenewErrorHandler, user?.profile.sub]);

    useEffect(() => {
        if (isAuthenticated) {
            const returnTo = sessionStorage.getItem("keycloak_return_to");
            if (returnTo != null && returnTo !== "") {
                sessionStorage.removeItem("keycloak_return_to");
                console.log("KeyCloakAuth: navigating to saved return path:", returnTo);
                navigate(returnTo, {replace: true});
            }
        }
    }, [isAuthenticated, navigate]);

    const getToken = useCallback(async () => {
        const forceChangeOnUserChange = userIdMemo;
        if (isAuthenticated ?? false) {
            return tokenRef.current;
        }
        return null;
    }, [tokenRef, isAuthenticated, userIdMemo]);

    const getHeaders = useCallback(async () => {
        const token = await getToken();
        const headers: Record<string, string> = {};
        if (token != null) {
            headers["Authorization"] = `Bearer ${token}`;
        }
        return headers;
    }, [getToken]);

    const isLoggedIn = useCallback(() => {
        return hasToken && isAuthenticated;
    }, [hasToken, isAuthenticated]);

    const getActiveAccount = useCallback(() => {
        if (user?.profile.sub == null || user?.profile.name == null) {
            return null
        }
        return {
            id: user?.profile.sub,
            name: user?.profile.name,
        };
    }, [user?.profile.sub, user?.profile.name]);

    const getUserId = useCallback(async () => {
        return userIdMemo;
    }, [userIdMemo]);

    const loginPopup = useCallback(async () => {
        const returnTo = location.pathname + location.search + location.hash;
        await signinRedirect({state: returnTo})
    }, [signinRedirect, location]);

    const logout = useCallback(async () => {
        await signoutSilent()
    }, [signoutSilent]);

    const refreshToken = useCallback(async () => {
        await signinSilent();
    }, [signinSilent]);

    return useMemo(() => {
        return {
            getToken,
            getHeaders,
            isLoggedIn,
            getActiveAccount,
            loginPopup,
            logout,
            canLogout: true,
            getUserId,
            refreshToken
        }
    }, [getToken, getHeaders, isLoggedIn, getActiveAccount, getUserId, loginPopup, logout, refreshToken]);

}